import "./project.scss";
import Mentions from "components/app/project/widgets/mentions";
import Heatmap from "components/app/project/heatmap";
import TopPosts from "components/app/project/top-posts";
import FiltersMenu from "components/app/project/filters-menu";
import { useDispatch, useSelector } from "react-redux";
import { ClipType, RootState } from "types";
import { useState, useEffect, useRef, useCallback } from "react";
import moment from "moment";
import { useParams } from "react-router-dom";
import DailyInsights from "components/app/project/daily-insights";
import TopAuthors from "components/app/project/widgets/top-authors";
import InfluencerDiscovery from "components/app/project/influencer-discovery";
import Subfolders from "components/app/project/subfolders";
import ClipTray from "components/app/clip-tray";
import Favourability from "components/app/project/favourability";
import WorldMap from "components/app/project/world-map";
import Mentor from "components/app/mentor";
import WordCloud from "components/app/project/word-cloud";
import DateRangeComparison from "components/app/project/date-range-comparison";
import ClusterMap from "components/app/project/cluster-map";
import api from "api";
import { useQuery } from "hooks/query";
import { actions } from "redux/reducers";

function Project() {
    const params: any = useParams();
    const query = useQuery();
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [dateFrom, setDateFrom] = useState(moment(query.get("datefrom")));
    const [dateTo, setDateTo] = useState(moment(query.get("dateto")).hour(23).minute(59).second(59));
    const [streamIds, setStreamIds] = useState<number[]>([]);
    const project = useSelector((state: RootState) => state.project);
    const [projectId, setProjectId] = useState<number>(params.projectId);
    const filtersMenuRef = useRef<any>();
    const [topAuthor, setTopAuthor] = useState<{ authorId: string; authorName: string; streamId: number } | null>(null);
    const [dateRangeComparison, setDateRangeComparison] = useState(false);
    const [clips, setClips] = useState<ClipType[]>([]);
    const dispatch = useDispatch();

    const setSubfolder = (subfolder: number) => {
        setProjectId(subfolder);
        dispatch(actions.selectedBin.set(subfolder));
    };

    const onAddAuthorToInfluencerDiscovery = (author: { authorId: string; authorName: string; streamId: number }) => {
        setTopAuthor(author);
    };

    const onDateRangeComparison = () => {
        setFiltersOpen(false);
        setDateRangeComparison(true);
    };

    const onRate = () => {
        getClips();
    };

    const getClips = useCallback(async () => {
        if (streamIds.length) {
            const data = await api.clip.getAll(projectId, dateFrom, dateTo, streamIds.join(","));
            setClips(data);
        }
    }, [projectId, dateFrom, dateTo, streamIds]);

    useEffect(() => {
        const streams = query.get("streams")!.split(",");
        if (streams.length === 1 && streams[0] === "10") {
            setStreamIds([10]);
        } else {
            setStreamIds(streams.filter((x) => x !== "10").map((x) => parseInt(x)));
        }
    }, [query]);

    useEffect(() => {
        getClips();
    }, [getClips]);

    useEffect(() => {
        dispatch(actions.selectedBin.set(params.projectId));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.projectId]);

    if (streamIds.length) {
        return (
            <div className="project">
                <div className="top">
                    <p className="project-name">{project.Description}</p>
                    <p className="project-type">{project.ProjectType}</p>
                    <span className="filters" ref={filtersMenuRef} onClick={() => setFiltersOpen(!filtersOpen)}>
                        Filter<i className={`fal ${filtersOpen ? "fa-times" : "fa-sliders-h"}`}></i>
                    </span>
                    <FiltersMenu
                        open={filtersOpen}
                        setOpen={setFiltersOpen}
                        dateFrom={dateFrom}
                        setDateFrom={setDateFrom}
                        dateTo={dateTo}
                        setDateTo={setDateTo}
                        streamIds={streamIds}
                        setStreamIds={setStreamIds}
                        projectTypeId={project?.ProjectTypeId}
                        showDatePicker={true}
                        buttonRef={filtersMenuRef}
                        dateRangeComparison={onDateRangeComparison}
                    />
                </div>
                <div className="body">
                    <div className={`mentions-date-range-comparison ${dateRangeComparison ? "open" : ""}`}>
                        <Mentions projectId={projectId} dateFrom={dateFrom} dateTo={dateTo} streamIds={streamIds} />
                        {dateRangeComparison && <DateRangeComparison dateFrom={dateFrom} dateTo={dateTo} streamIds={streamIds} onClose={() => setDateRangeComparison(false)} projectId={projectId} />}
                    </div>
                    <Heatmap projectId={projectId} dateFrom={dateFrom} dateTo={dateTo} streamIds={streamIds} />
                    <TopPosts projectId={projectId} dateFrom={dateFrom} dateTo={dateTo} streamIds={streamIds} />
                    {[1, 5, 6, 7].includes(project?.ProjectTypeId) && <TopAuthors projectId={projectId} dateFrom={dateFrom} dateTo={dateTo} streamIds={streamIds} onAddToInfluencerDiscovery={onAddAuthorToInfluencerDiscovery} />}
                    {[1, 5, 6, 7].includes(project?.ProjectTypeId) && <InfluencerDiscovery projectId={projectId} dateFrom={dateFrom} dateTo={dateTo} streamIds={streamIds} topAuthor={topAuthor} />}
                    <DailyInsights projectId={projectId} dateFrom={dateFrom} dateTo={dateTo} streamIds={streamIds} projectTypeId={project?.ProjectTypeId} section="project" />
                    <Subfolders projectId={params.projectId} dateFrom={dateFrom} dateTo={dateTo} streamIds={streamIds} setSubfolder={setSubfolder} />
                    <WordCloud projectId={projectId} dateFrom={dateFrom} dateTo={dateTo} streamIds={streamIds} />
                    <Favourability clips={clips} />
                    {/* {[1, 5, 6, 7].includes(project?.ProjectTypeId) && <WorldMap projectId={projectId} dateFrom={dateFrom} dateTo={dateTo} streamIds={streamIds} />} */}
                    {/* {[1, 5, 6, 7].includes(project?.ProjectTypeId) && <ClusterMap projectId={projectId} dateFrom={dateFrom} dateTo={dateTo} streamIds={streamIds} />} */}
                </div>
                <ClipTray onRate={onRate} />
                {/* <Mentor pathName={project?.ProjectType.trim().toLocaleLowerCase().replace(/\s/g, "-")} /> */}
            </div>
        );
    } else {
        return <></>;
    }
}

export default Project;
